import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { bulkCaseAssignment, getEligibleUws } from './api';
import type { BulkCaseAssignmentResponse, EligibleUwItem } from './types';

const DEFAULT_OVERRIDE_REASON = 'Smart retry manual assignment';

export interface AssignModalProps {
  applicationIds: number[];
  open: boolean;
  onClose: (clearSelection?: boolean) => void;
  /**
   * Called with the cases that were assigned, so the caller can stop tracking them. A partly failed
   * request keeps the modal open, so `onClose(true)` alone would leave the assigned cases selected.
   */
  onAssigned?: (applicationIds: number[]) => void;
  /** React Query cache key to invalidate on successful assignment. */
  invalidateKey: string;
  /** Override reason options rendered as menu items. Omit to hide the field entirely (a default reason is sent instead). */
  overrideReasons?: string[];
}

export function AssignModal({
  applicationIds,
  open,
  onClose,
  onAssigned,
  invalidateKey,
  overrideReasons,
}: AssignModalProps) {
  const queryClient = useQueryClient();
  const [selectedUwId, setSelectedUwId] = useState<number | ''>('');
  const [overrideReason, setOverrideReason] = useState('');
  const [otherReasonText, setOtherReasonText] = useState('');
  const [partialResult, setPartialResult] =
    useState<BulkCaseAssignmentResponse | null>(null);

  const showOverrideReason = !!overrideReasons?.length;
  const isOtherSelected = overrideReason === 'Other';
  const effectiveReason = !showOverrideReason
    ? DEFAULT_OVERRIDE_REASON
    : isOtherSelected
      ? otherReasonText.trim() || 'Other'
      : overrideReason;

  const {
    data: eligibleData,
    isLoading: loadingUws,
    isError: eligibleUwsFailed,
    error: eligibleUwsError,
  } = useQuery({
    queryKey: ['manager-tab-eligible-uws'],
    queryFn: getEligibleUws,
    enabled: open,
    refetchOnWindowFocus: false,
  });

  const { mutate, isLoading, isError, error } = useMutation({
    mutationFn: () =>
      bulkCaseAssignment({
        applicationIds,
        targetUwId: selectedUwId as number,
        overrideReason: effectiveReason,
      }),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: [invalidateKey] });
      onAssigned?.(
        data.results
          .filter(result => result.success)
          .map(result => result.applicationId),
      );
      if (data.failureCount > 0) {
        setPartialResult(data);
        return;
      }
      setSelectedUwId('');
      setOverrideReason('');
      setOtherReasonText('');
      setPartialResult(null);
      onClose(true);
    },
  });

  const handleClose = () => {
    setSelectedUwId('');
    setOverrideReason('');
    setOtherReasonText('');
    setPartialResult(null);
    onClose();
  };

  const canSubmit =
    selectedUwId !== '' &&
    effectiveReason.length > 0 &&
    effectiveReason.length <= 500 &&
    !isLoading;

  const failedApplicationIds =
    partialResult?.results
      .filter(result => !result.success)
      .map(result => result.applicationId) ?? [];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Assign {applicationIds.length} case
        {applicationIds.length !== 1 ? 's' : ''} to UW
      </DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}
      >
        {isError && (
          <Alert severity="error">
            {(error as Error)?.message ?? 'Assignment failed.'}
          </Alert>
        )}
        {eligibleUwsFailed && (
          <Alert severity="error">
            {(eligibleUwsError as Error)?.message ??
              'Unable to load underwriters.'}
          </Alert>
        )}
        {partialResult && (
          <Alert severity="warning">
            Assigned {partialResult.successCount} of{' '}
            {partialResult.results.length} cases.
            {failedApplicationIds.length > 0 && (
              <Box component="span" sx={{ display: 'block', mt: 1 }}>
                Failed application IDs: {failedApplicationIds.join(', ')}
              </Box>
            )}
          </Alert>
        )}
        <FormControl
          fullWidth
          disabled={loadingUws || eligibleUwsFailed || isLoading}
        >
          <InputLabel>Select Underwriter</InputLabel>
          <Select
            value={selectedUwId}
            label="Select Underwriter"
            onChange={e => setSelectedUwId(e.target.value as number)}
          >
            {(eligibleData?.underwriters ?? []).map((uw: EligibleUwItem) => (
              <MenuItem key={uw.id} value={uw.id}>
                {uw.name ?? uw.email} — plate {uw.currentPlate}/
                {uw.dailyPointCap}
                {uw.status === 'paused' && ' (Paused)'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {showOverrideReason && (
          <FormControl fullWidth disabled={isLoading}>
            <InputLabel>Override Reason</InputLabel>
            <Select
              value={overrideReason}
              label="Override Reason"
              onChange={e => setOverrideReason(e.target.value)}
            >
              {overrideReasons!.map(r => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {showOverrideReason && isOtherSelected && (
          <TextField
            fullWidth
            label="Please specify"
            value={otherReasonText}
            onChange={e => setOtherReasonText(e.target.value)}
            error={otherReasonText.length > 500}
            helperText={
              otherReasonText.length > 500
                ? 'Override reason cannot exceed 500 characters'
                : undefined
            }
            disabled={isLoading}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          {partialResult ? 'Close' : 'Cancel'}
        </Button>
        {!partialResult && (
          <Button
            onClick={() => mutate()}
            variant="contained"
            disabled={!canSubmit}
          >
            {isLoading ? <CircularProgress size={18} /> : 'Assign'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
